import React, { useState, useRef } from 'react';
import { useAuth, createApiClient } from '../App';
import { toast } from 'sonner';
import {
  BookOpen,
  SpinnerGap,
  Check,
  X,
  Camera,
  CurrencyCircleDollar,
  ForkKnife,
  CaretDown,
  CaretUp,
  MapPin
} from '@phosphor-icons/react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { Button } from './ui/button';
import { Badge } from './ui/badge';

const MenuScanModal = ({ open, onOpenChange }) => {
  const { token } = useAuth();
  const api = createApiClient(token);
  const fileInputRef = useRef(null);

  const [step, setStep] = useState('upload'); // upload | scanning | results
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [result, setResult] = useState(null);
  const [expandedSections, setExpandedSections] = useState({});

  const resetState = () => {
    setStep('upload');
    setImageFile(null);
    setImagePreview(null);
    setResult(null);
    setExpandedSections({});
  };

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImageFile(file);
    const reader = new FileReader();
    reader.onload = (ev) => setImagePreview(ev.target.result);
    reader.readAsDataURL(file);
  };

  const handleScan = async () => {
    if (!imageFile) return;
    setStep('scanning');

    const formData = new FormData();
    formData.append('file', imageFile);

    try {
      const res = await api.post('/menus/scan', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setResult(res.data);
      // Expand all sections by default
      const expanded = {};
      (res.data.sections || []).forEach((_, i) => { expanded[i] = true; });
      setExpandedSections(expanded);
      setStep('results');
    } catch (error) {
      toast.error('Failed to scan menu');
      setStep('upload');
    }
  };

  const toggleSection = (idx) => {
    setExpandedSections(prev => ({ ...prev, [idx]: !prev[idx] }));
  };

  const totalItems = result?.sections?.reduce((acc, s) => acc + (s.items?.length || 0), 0) || 0;

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) resetState(); onOpenChange(o); }}>
      <DialogContent className="sm:max-w-[560px] max-h-[90vh] overflow-y-auto" data-testid="menu-scan-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <BookOpen weight="duotone" className="w-5 h-5 text-[#4B5945]" />
            {step === 'upload' ? 'Scan Menu' : step === 'scanning' ? 'Reading menu...' : 'Menu Extracted'}
          </DialogTitle>
        </DialogHeader>

        {/* Upload Step */}
        {step === 'upload' && (
          <div className="space-y-4">
            <p className="text-sm text-[#7A7873]">
              Photograph a restaurant menu — AI will extract every dish and price.
            </p>
            {imagePreview ? (
              <div className="relative">
                <img src={imagePreview} alt="Menu" className="w-full h-48 object-cover rounded-xl" />
                <button
                  onClick={() => { setImageFile(null); setImagePreview(null); }}
                  className="absolute top-2 right-2 p-1.5 bg-white/90 rounded-full"
                >
                  <X weight="bold" className="w-4 h-4 text-red-500" />
                </button>
              </div>
            ) : (
              <button
                onClick={() => fileInputRef.current?.click()}
                className="w-full h-48 border-2 border-dashed border-[#E8DCD0] rounded-xl flex flex-col items-center justify-center hover:border-[#4B5945]/40 hover:bg-[#F9F8F5] transition-colors"
                data-testid="menu-upload-area"
              >
                <BookOpen weight="duotone" className="w-10 h-10 text-[#B5B0AA] mb-2" />
                <p className="text-sm text-[#7A7873]">Tap to photograph a menu</p>
                <p className="text-xs text-[#B5B0AA] mt-1">Works with printed menus, chalkboards, digital screens</p>
              </button>
            )}
            <input ref={fileInputRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={handleFileSelect} />
            <Button
              onClick={handleScan}
              disabled={!imageFile}
              className="w-full bg-[#4B5945] hover:bg-[#3d4a38] text-white"
              data-testid="scan-menu-btn"
            >
              <BookOpen weight="bold" className="w-5 h-5 mr-2" />
              Extract Menu
            </Button>
          </div>
        )}

        {/* Scanning Step */}
        {step === 'scanning' && (
          <div className="flex flex-col items-center py-12">
            <SpinnerGap className="w-10 h-10 animate-spin text-[#4B5945] mb-4" />
            <p className="text-sm text-[#7A7873]">AI is reading the menu...</p>
            <p className="text-xs text-[#B5B0AA] mt-1">Extracting dishes, prices, and sections</p>
          </div>
        )}

        {/* Results Step */}
        {step === 'results' && result && (
          <div className="space-y-4">
            {/* Header stats */}
            <div className="flex items-center gap-3 flex-wrap">
              {result.restaurant_name && (
                <Badge className="bg-[#F5F1EC] text-[#1A1918]">
                  <ForkKnife weight="fill" className="w-3 h-3 mr-1" />
                  {result.restaurant_name}
                </Badge>
              )}
              {result.matched_restaurant && (
                <Badge className="bg-[#D1E2C4] text-[#4B5945]">
                  <Check weight="bold" className="w-3 h-3 mr-1" />
                  Matched
                </Badge>
              )}
              <Badge variant="secondary">{totalItems} items</Badge>
              {result.price_range && (
                <Badge variant="secondary">
                  <CurrencyCircleDollar weight="fill" className="w-3 h-3 mr-1" />
                  R{result.price_range.min} — R{result.price_range.max}
                </Badge>
              )}
            </div>

            {/* Menu sections */}
            <div className="space-y-2" data-testid="menu-sections">
              {(result.sections || []).map((section, idx) => (
                <div key={idx} className="border border-[#EAE8E3] rounded-lg overflow-hidden">
                  <button
                    onClick={() => toggleSection(idx)}
                    className="w-full flex items-center justify-between px-4 py-3 bg-[#F9F8F5] hover:bg-[#F5F1EC] transition-colors"
                    data-testid={`menu-section-${idx}`}
                  >
                    <span className="font-medium text-sm text-[#1A1918]">{section.name || `Section ${idx + 1}`}</span>
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-[#9A9590]">{section.items?.length || 0} items</span>
                      {expandedSections[idx] ? (
                        <CaretUp weight="bold" className="w-4 h-4 text-[#7A7873]" />
                      ) : (
                        <CaretDown weight="bold" className="w-4 h-4 text-[#7A7873]" />
                      )}
                    </div>
                  </button>
                  {expandedSections[idx] && (
                    <div className="divide-y divide-[#EAE8E3]">
                      {(section.items || []).map((item, iIdx) => (
                        <div key={iIdx} className="px-4 py-2.5 flex items-start justify-between gap-3">
                          <div className="min-w-0">
                            <p className="text-sm font-medium text-[#1A1918]">{item.name}</p>
                            {item.description && (
                              <p className="text-xs text-[#9A9590] mt-0.5 line-clamp-2">{item.description}</p>
                            )}
                          </div>
                          {item.price && (
                            <span className="text-sm font-semibold text-[#4B5945] flex-shrink-0">
                              R{item.price}
                            </span>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>

            {/* Specials */}
            {result.specials && result.specials.length > 0 && (
              <div className="border border-[#C4A35A]/30 rounded-lg p-4 bg-[#C4A35A]/5" data-testid="menu-specials">
                <h3 className="text-sm font-semibold text-[#C4A35A] mb-2">Specials</h3>
                {result.specials.map((s, i) => (
                  <div key={i} className="flex items-center justify-between py-1">
                    <div>
                      <span className="text-sm text-[#1A1918]">{s.name}</span>
                      {s.note && <span className="text-xs text-[#9A9590] ml-2">{s.note}</span>}
                    </div>
                    {s.price && <span className="text-sm font-semibold text-[#4B5945]">R{s.price}</span>}
                  </div>
                ))}
              </div>
            )}

            <div className="flex gap-2">
              <Button variant="outline" onClick={resetState} className="flex-1" data-testid="menu-scan-another">
                Scan Another
              </Button>
              <Button onClick={() => onOpenChange(false)} className="flex-1 bg-[#4B5945] hover:bg-[#3d4a38] text-white" data-testid="menu-done">
                Done
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default MenuScanModal;
